import { Fragment, useEffect, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { api } from '@/runtime'
import type { Analytics, AnalyticsDays, AnalyticsQuery, Session, Trend } from '@/db/types'
import { Button, Empty, Failed, PageTitle, SectionTitle, Segmented, cx } from '@/components/ui'
import { PlayButton } from '@/components/Player'
import { Busy, SkeletonInsights } from '@/components/Skeleton'
import { useWidth } from '@/hooks/useWidth'
import { Cover, LibraryHeader } from './Music'
import type { PlayRequest } from '@/db/types'

/**
 * Library insights: what the points say about one metric over a window,
 * narrowed to an artist, album or track when the URL says so. The server
 * does the arithmetic (one engine for listening, playtime and whatever
 * comes next); this page draws it and links every name to its own view.
 * Charts are inline SVG or grids, so nothing loads from outside.
 */
const PRESETS: { value: AnalyticsDays; label: string }[] = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  // the last twelve months; a calendar year is the period picker's Year
  { value: 365, label: '12 months' },
  { value: 0, label: 'All time' },
]
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HIGHLIGHT_ICON: Record<string, string> = {
  size: '🎧',
  days: '📅',
  change: '📈',
  when: '⏰',
  artist: '🏆',
  item: '🎵',
  constant: '📌',
  returning: '↩️',
  new: '✨',
  sessions: '🔁',
}
const PARTS: Record<string, { label: string; hours: string; icon: string }> = {
  night: { label: 'Night', hours: '00–06', icon: '🌙' },
  morning: { label: 'Morning', hours: '06–12', icon: '🌅' },
  afternoon: { label: 'Afternoon', hours: '12–18', icon: '☀️' },
  evening: { label: 'Evening', hours: '18–24', icon: '🌆' },
}

const fmtHours = (minutes: number) => {
  if (minutes < 60) return `${Math.round(minutes)} min`
  const h = minutes / 60
  return h < 10 ? `${h.toFixed(1)} h` : `${Math.round(h)} h`
}
const fmtNum = (n: number) => n.toLocaleString()
const one = (n: number, unit: string) => (n === 1 ? unit.replace(/s$/, '') : unit)
/** Sentence case for a label made from a word the engine sends ("plays" → "Plays"). */
const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1)
const local = (iso: string) => new Date(iso.length === 10 ? iso + 'T12:00:00' : iso)
const fmtDate = (iso: string, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }) =>
  local(iso).toLocaleDateString(undefined, opts)
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Links for the things the engine names. */
export const artistTo = (name: string) => ({
  to: '/music/insights' as const,
  search: { artist: name } as never,
})
export const albumTo = (name: string, artist?: string) => ({
  to: '/music/insights' as const,
  search: { album: name, ...(artist ? { artist } : {}) } as never,
})
export const itemTo = (id: string) => ({ to: '/music/insights' as const, search: { item: id } as never })

/** "+12%" against the previous window, or nothing when there is nothing to compare with. */
const Delta = ({ now, before }: { now: number; before: number }) => {
  if (!before || now === before) return null
  const pct = Math.round(((now - before) / before) * 100)
  return (
    <span
      className={cx('text-[11px] tabular-nums', pct > 0 ? 'text-emerald-500' : 'text-rose-500')}
      title="against the window before"
    >
      {pct > 0 ? '+' : ''}
      {pct}%
    </span>
  )
}

const Tile = ({
  label,
  value,
  sub,
  delta,
}: {
  label: string
  value: string
  sub?: string
  delta?: { now: number; before: number }
}) => (
  <div className="px-3.5 py-3 min-w-0">
    <div className="text-[13px] font-semibold text-muted truncate">{label}</div>
    <div className="text-2xl font-semibold tracking-tight tabular-nums mt-0.5 truncate">{value}</div>
    {sub && <div className="text-xs text-faint mt-0.5 leading-snug">{sub}</div>}
    {delta && (
      <div className="text-xs leading-snug">
        <Delta now={delta.now} before={delta.before} />
      </div>
    )}
  </div>
)

/** A row of tiles as one surface, hairlines between them (never a card each). */
const Tiles = ({ children, testId }: { children: React.ReactNode; testId?: string }) => (
  <div className="card grid grid-cols-3 divide-x divide-line overflow-hidden mb-5" data-testid={testId}>
    {children}
  </div>
)

/** An area chart of the series; hover reads a bucket, click zooms into it. */
const Area = ({
  series,
  unit,
  bucket,
  onPick,
}: {
  series: { at: string; count: number; total: number }[]
  unit: string
  bucket: string
  onPick?: (at: string) => void
}) => {
  const [hover, setHover] = useState<number | null>(null)
  // drawn at the column's real width: a fixed viewBox would sit centred in a
  // wide column and the hover maths would read the wrong bucket
  const { ref, width } = useWidth<HTMLDivElement>()
  const W = Math.max(320, Math.round(width))
  const H = 160
  const pad = { l: 4, r: 4, t: 10, b: 22 }
  if (series.length === 0)
    return (
      <div className="h-40 flex items-center justify-center text-sm text-faint">Nothing in this window.</div>
    )
  const max = Math.max(1, ...series.map((s) => s.count))
  const n = series.length
  const x = (i: number) => pad.l + (n === 1 ? (W - pad.l - pad.r) / 2 : (i * (W - pad.l - pad.r)) / (n - 1))
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / max)
  const line = series
    .map((s, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(s.count).toFixed(1)}`)
    .join(' ')
  const area = `${line} L${x(n - 1).toFixed(1)},${(H - pad.b).toFixed(1)} L${x(0).toFixed(1)},${(H - pad.b).toFixed(1)} Z`
  const labelEvery = Math.max(1, Math.ceil(n / 6))
  const peak = series.reduce((a, b) => (b.count > a.count ? b : a), series[0]!)
  const h = hover !== null ? series[hover] : null
  const bucketLabel = (at: string) =>
    fmtDate(
      at,
      bucket === 'month'
        ? { month: 'long', year: 'numeric' }
        : bucket === 'week'
          ? { day: 'numeric', month: 'short', year: '2-digit' }
          : { weekday: 'short', day: 'numeric', month: 'short' },
    )
  return (
    <div ref={ref}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        className={cx('block h-40 max-w-full', onPick && 'cursor-pointer')}
        role="img"
        aria-label={`${unit} per ${bucket}`}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect()
          const px = ((e.clientX - r.left) / r.width) * W
          setHover(Math.max(0, Math.min(n - 1, Math.round(((px - pad.l) / (W - pad.l - pad.r)) * (n - 1)))))
        }}
        onMouseLeave={() => setHover(null)}
        onClick={() => h && onPick?.(h.at)}
      >
        <defs>
          <linearGradient id="insights-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="var(--color-accent)" stopOpacity="0.35" />
            <stop offset="1" stopColor="var(--color-accent)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#insights-fill)" />
        <path
          d={line}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {n <= 60 &&
          series.map((s, i) => (
            <circle key={s.at} cx={x(i)} cy={y(s.count)} r={n <= 14 ? 3 : 2} fill="var(--color-accent)" />
          ))}
        {hover !== null && (
          <>
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={pad.t}
              y2={H - pad.b}
              stroke="var(--color-fg)"
              strokeOpacity="0.25"
            />
            <circle cx={x(hover)} cy={y(series[hover]!.count)} r={4} fill="var(--color-fg)" />
          </>
        )}
        {series.map((s, i) =>
          // the last day always, and no regular label crowding it
          (i % labelEvery === 0 && n - 1 - i >= labelEvery / 2) || i === n - 1 ? (
            <text
              key={`l-${s.at}`}
              x={x(i)}
              y={H - 6}
              textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
              className="fill-[var(--color-faint)]"
              fontSize="11"
            >
              {fmtDate(
                s.at,
                bucket === 'month' ? { month: 'short', year: '2-digit' } : { day: 'numeric', month: 'short' },
              )}
            </text>
          ) : null,
        )}
      </svg>
      <div className="text-xs text-faint mt-1 tabular-nums" data-testid="insights-series-note">
        {h ? (
          <>
            {bucketLabel(h.at)}: {fmtNum(h.count)} {unit}
            {h.total >= 1 ? `, ${fmtHours(h.total)}` : ''}
            {onPick ? '. Click to zoom in.' : ''}
          </>
        ) : (
          <>
            Peak {fmtNum(peak.count)} {unit} on {bucketLabel(peak.at)}
            {bucket !== 'day' ? ` (by ${bucket})` : ''}
          </>
        )}
      </div>
    </div>
  )
}

/** Weekday by hour, opacity by share of the busiest cell. */
const Heatmap = ({
  cells,
  unit,
}: {
  cells: { dow: number; hour: number; count: number }[]
  unit: string
}) => {
  const byKey = new Map(cells.map((c) => [`${c.dow}-${c.hour}`, c.count]))
  const max = Math.max(1, ...cells.map((c) => c.count))
  const busiest = cells.reduce<{ dow: number; hour: number; count: number } | null>(
    (a, b) => (!a || b.count > a.count ? b : a),
    null,
  )
  return (
    <div data-testid="insights-heatmap">
      <div className="grid gap-[3px]" style={{ gridTemplateColumns: '2.2rem repeat(24, minmax(0, 1fr))' }}>
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} className="text-[9px] text-faint text-center leading-none">
            {h % 3 === 0 ? h : ''}
          </span>
        ))}
        {DOW.map((d, i) => (
          <Fragment key={d}>
            <span className="text-[10px] text-faint leading-none self-center">{d}</span>
            {Array.from({ length: 24 }, (_, h) => {
              const v = byKey.get(`${i + 1}-${h}`) ?? 0
              return (
                <span
                  key={`${d}-${h}`}
                  className="aspect-square rounded-[3px] bg-accent"
                  style={{ opacity: v ? 0.15 + 0.85 * (v / max) : 0.06 }}
                  title={`${d} ${String(h).padStart(2, '0')}:00, ${v} ${unit}`}
                />
              )
            })}
          </Fragment>
        ))}
      </div>
      {busiest && (
        <div className="text-xs text-faint mt-2">
          Busiest single hour: {DOW[busiest.dow - 1]} around {String(busiest.hour).padStart(2, '0')}:00
        </div>
      )}
    </div>
  )
}

const BarRow = ({
  image,
  icon,
  round,
  title,
  sub,
  value,
  share,
  to,
  testId,
  play,
  uri,
}: {
  image?: string
  icon?: string
  round?: boolean
  title: string
  sub?: React.ReactNode
  value: string
  share: number
  to?: { to: '/music/insights'; search: never }
  testId?: string
  play?: PlayRequest
  uri?: string
}) => {
  const body = (
    <>
      <Cover image={image} icon={icon} round={round} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="text-sm truncate">{title}</span>
          <span className="text-xs text-muted tabular-nums shrink-0">{value}</span>
        </span>
        {sub && <span className="flex gap-x-2.5 min-w-0 text-xs text-faint truncate">{sub}</span>}
        <span className="block h-1 rounded-full bg-line mt-1.5 overflow-hidden">
          <span
            className="block h-full bg-accent rounded-full"
            style={{ width: `${Math.max(3, share * 100)}%` }}
          />
        </span>
      </span>
      {play && <PlayButton small play={play} title={title} uri={uri} />}
    </>
  )
  return (
    <li data-testid={testId}>
      {to ? (
        <Link
          {...to}
          className="flex items-center gap-3 px-3 py-2 hover:bg-raised/60 rounded-[var(--radius-card-inner)]"
        >
          {body}
        </Link>
      ) : (
        <div className="flex items-center gap-3 px-3 py-2">{body}</div>
      )}
    </li>
  )
}

const SessionRow = ({ s, unit }: { s: Session; unit: string }) => (
  <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
    <span className="min-w-0">
      <span className="block truncate">
        {fmtDate(s.started, { weekday: 'short', day: 'numeric', month: 'short' })},{' '}
        {local(s.started).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
      </span>
      <span className="flex flex-wrap gap-x-2.5 text-xs text-faint">
        <span>
          {fmtNum(s.plays)} {one(s.plays, unit)}
        </span>
        <span>{s.items} different</span>
        {s.albums === 1 && s.plays > 1 && <span>one album</span>}
      </span>
    </span>
    <span className="text-xs text-muted tabular-nums shrink-0">{fmtHours(s.minutes)}</span>
  </li>
)

const TrendList = ({
  title,
  rows,
  empty,
  unit,
}: {
  title: string
  rows: Trend[]
  empty: string
  unit: string
}) => (
  <div>
    <SectionTitle>{title}</SectionTitle>
    <ul className="group-list">
      {rows.map((t) => (
        <li key={t.key}>
          <Link
            {...artistTo(t.key)}
            className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-raised/60"
          >
            <span className="truncate">{t.key}</span>
            <span className="text-xs text-muted tabular-nums shrink-0">
              {t.before ? `${fmtNum(t.before)} → ` : ''}
              {fmtNum(t.count)} {unit}
            </span>
          </Link>
        </li>
      ))}
      {rows.length === 0 && <li className="px-3 py-2 text-sm text-faint">{empty}</li>}
    </ul>
  </div>
)

/** Stacked bars of the top genres per bucket. */
const GenreFlow = ({
  buckets,
  genres,
}: {
  buckets: { at: string; counts: Record<string, number> }[]
  genres: string[]
}) => {
  if (buckets.length < 2) return null
  const max = Math.max(1, ...buckets.map((b) => Object.values(b.counts).reduce((a, c) => a + c, 0)))
  const hues = [0, 60, 120, 180, 240, 300]
  return (
    <div>
      <div className="flex items-end gap-[3px] h-28">
        {buckets.map((b) => {
          const total = Object.values(b.counts).reduce((a, c) => a + c, 0)
          return (
            <div
              key={b.at}
              className="flex-1 flex flex-col-reverse min-w-0"
              style={{ height: `${(total / max) * 100}%` }}
              title={`${fmtDate(b.at, { month: 'short', year: '2-digit' })}: ${genres.map((g) => `${g} ${b.counts[g] ?? 0}`).join(', ')}`}
            >
              {genres.map((g, i) => {
                const v = b.counts[g] ?? 0
                return v ? (
                  <div
                    key={g}
                    style={{
                      height: `${(v / Math.max(1, total)) * 100}%`,
                      background: `color-mix(in oklab, var(--color-accent) ${100 - i * 14}%, hsl(${hues[i]} 60% 60%))`,
                    }}
                  />
                ) : null
              })}
            </div>
          )
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-faint">
        {genres.map((g, i) => (
          <span key={g} className="inline-flex items-center gap-1">
            <span
              className="w-2 h-2 rounded-sm"
              style={{
                background: `color-mix(in oklab, var(--color-accent) ${100 - i * 14}%, hsl(${hues[i]} 60% 60%))`,
              }}
            />
            {g}
          </span>
        ))}
      </div>
    </div>
  )
}

/** Where in time: presets, or a month / year browser, or a custom range. */
const PeriodPicker = ({
  q,
  set,
  firstAt,
}: {
  q: AnalyticsQuery
  set: (patch: Partial<AnalyticsQuery>) => void
  firstAt?: string
}) => {
  const isRange = !!(q.since && q.until)
  const [mode, setMode] = useState<'preset' | 'month' | 'year' | 'custom'>(isRange ? 'month' : 'preset')
  const since = q.since ? local(q.since) : new Date()
  const monthOf = (d: Date) => ({
    since: ymd(new Date(d.getFullYear(), d.getMonth(), 1)),
    until: ymd(new Date(d.getFullYear(), d.getMonth() + 1, 1)),
  })
  const yearOf = (y: number) => ({ since: `${y}-01-01`, until: `${y + 1}-01-01` })
  const firstYear = firstAt ? local(firstAt).getFullYear() : new Date().getFullYear() - 3
  const years = Array.from({ length: new Date().getFullYear() - firstYear + 1 }, (_, i) => firstYear + i)
  const go = (r: { since: string; until: string }) => set({ days: undefined, ...r })
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="insights-period">
      <Segmented
        label="How to pick a period"
        value={mode}
        onChange={(m) => {
          setMode(m)
          if (m === 'preset') set({ since: undefined, until: undefined, days: 30 })
          if (m === 'month') go(monthOf(new Date()))
          if (m === 'year') go(yearOf(new Date().getFullYear()))
        }}
        options={[
          { value: 'preset' as const, label: 'Recent' },
          { value: 'month' as const, label: 'Month' },
          { value: 'year' as const, label: 'Year' },
          { value: 'custom' as const, label: 'Custom' },
        ]}
      />
      {mode === 'preset' && (
        <Segmented
          label="Window"
          value={q.days ?? 30}
          onChange={(v) => set({ days: v, since: undefined, until: undefined })}
          options={PRESETS}
        />
      )}
      {mode === 'month' && isRange && (
        <span className="inline-flex items-center gap-1">
          <Button
            variant="chip"
            onClick={() => go(monthOf(new Date(since.getFullYear(), since.getMonth() - 1, 1)))}
            aria-label="Previous month"
          >
            ‹
          </Button>
          <span className="text-sm min-w-[8.5rem] text-center tabular-nums" data-testid="insights-month">
            {MONTHS[since.getMonth()]} {since.getFullYear()}
          </span>
          <Button
            variant="chip"
            onClick={() => go(monthOf(new Date(since.getFullYear(), since.getMonth() + 1, 1)))}
            aria-label="Next month"
          >
            ›
          </Button>
        </span>
      )}
      {mode === 'year' && (
        <Segmented
          label="Year"
          value={q.since ? local(q.since).getFullYear() : new Date().getFullYear()}
          onChange={(y) => go(yearOf(y))}
          options={years.map((y) => ({ value: y, label: String(y) }))}
        />
      )}
      {mode === 'custom' && (
        <form
          className="inline-flex items-center gap-2 text-sm"
          onSubmit={(e) => {
            e.preventDefault()
            const f = new FormData(e.currentTarget)
            const a = String(f.get('since') ?? '')
            const b = String(f.get('until') ?? '')
            if (a && b && b > a) go({ since: a, until: b })
          }}
        >
          <input type="date" name="since" defaultValue={q.since} className="bare-input" aria-label="From" />
          <span className="text-faint">to</span>
          <input type="date" name="until" defaultValue={q.until} className="bare-input" aria-label="To" />
          <Button variant="chip" type="submit">
            Go
          </Button>
        </form>
      )}
    </div>
  )
}

/**
 * The metrics held, remembered across reads (and in this device's storage),
 * so the Listening / Focusing switch stays when a read fails. Before any read
 * has answered, the two the app always writes are offered.
 */
type MetricChoice = { key: string; label: string }
const METRICS_KEY = 'insights-metrics'
const FALLBACK_METRICS: MetricChoice[] = [
  { key: 'listened', label: 'Listening' },
  { key: 'focused', label: 'Focusing' },
]
const knownMetrics = (): MetricChoice[] => {
  try {
    const held = JSON.parse(localStorage.getItem(METRICS_KEY) ?? 'null') as MetricChoice[] | null
    if (Array.isArray(held) && held.length) return held
  } catch {
    /* no storage: the fallback */
  }
  return FALLBACK_METRICS
}
const rememberMetrics = (available: { key: string; label: string }[]): MetricChoice[] => {
  const list = available.map((a) => ({ key: a.key, label: a.label }))
  try {
    localStorage.setItem(METRICS_KEY, JSON.stringify(list))
  } catch {
    /* a convenience only */
  }
  return list
}

export const InsightsView = () => {
  const search = useSearch({ strict: false }) as AnalyticsQuery
  const navigate = useNavigate()
  const q: AnalyticsQuery = {
    metric: search.metric ?? 'listened',
    // a thing's own page opens on everything it has; the overview on the last 30 days
    days:
      search.since && search.until
        ? undefined
        : ((search.days ?? (search.artist || search.album || search.item ? 0 : 30)) as AnalyticsDays),
    since: search.since,
    until: search.until,
    artist: search.artist,
    album: search.album,
    item: search.item,
  }
  const [data, setData] = useState<Analytics | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [nudge, setNudge] = useState(0)
  // the metrics to switch between outlive a failed read, so one that fails
  // never takes the others out of reach
  const [metrics, setMetrics] = useState(knownMetrics)
  // which query the data on screen answers; while it lags the URL, the page is busy
  const [answered, setAnswered] = useState<string | null>(null)
  const key = JSON.stringify(q)
  const loading = answered !== key
  useEffect(() => {
    const qq = JSON.parse(key) as AnalyticsQuery
    let stale = false
    api
      .libraryAnalytics(qq)
      .then((a) => {
        if (stale) return
        setData(a)
        setError(null)
        setAnswered(key)
        setMetrics(rememberMetrics(a.available))
      })
      .catch((e: unknown) => {
        if (stale) return
        setError(e instanceof Error ? e.message : 'Failed')
        setAnswered(key)
      })
    return () => {
      stale = true
    }
  }, [key, nudge])
  const set = (patch: Partial<AnalyticsQuery>) => {
    const next: Record<string, unknown> = { ...q, ...patch }
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k]
    if (next.since && next.until) delete next.days
    void navigate({ to: '/music/insights', search: next as never, replace: true })
  }
  const zoom = (at: string) => {
    if (!data) return
    const d = local(at)
    const end =
      data.bucket === 'month'
        ? new Date(d.getFullYear(), d.getMonth() + 1, 1)
        : data.bucket === 'week'
          ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7)
          : new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
    if (data.bucket === 'day') return
    set({ days: undefined, since: ymd(d), until: ymd(end) })
  }

  const m = data?.metric
  const isCum = m?.mode === 'cumulative'
  const unit = m?.unit ?? 'plays'
  const sub = data?.subject
  const icon = m?.item_kind === 'game' ? '🎮' : m?.item_kind === 'film' ? '🎬' : '♪'
  const minutesSub = (t: { count: number; total: number; valued: number }) =>
    !t.total
      ? undefined
      : t.valued >= t.count
        ? `${fmtHours(t.total)} in total`
        : `${fmtHours(t.total)} over ${fmtNum(t.valued)} timed ${unit}`
  const periodLabel = (() => {
    if (!data) return ''
    if (q.since && q.until) {
      const a = local(q.since)
      const b = new Date(local(q.until).getTime() - 1)
      if (
        a.getDate() === 1 &&
        b.getMonth() === a.getMonth() &&
        b.getFullYear() === a.getFullYear() &&
        local(q.until).getDate() === 1
      )
        return `${MONTHS[a.getMonth()]} ${a.getFullYear()}`
      if (a.getMonth() === 0 && a.getDate() === 1 && b.getMonth() === 11 && b.getDate() === 31)
        return String(a.getFullYear())
      return `${fmtDate(q.since, { day: 'numeric', month: 'short', year: 'numeric' })} to ${b.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`
    }
    return q.days ? `The last ${q.days} days` : 'All time'
  })()

  return (
    <div data-testid="insights">
      {!sub && <LibraryHeader />}
      {sub && (
        <PageTitle
          eyebrow={
            sub ? (
              <Link to="/music/insights" search={{ days: 30 } as never} className="hover:underline">
                ← Insights
              </Link>
            ) : undefined
          }
          right={
            sub ? (
              <PlayButton
                play={
                  sub.kind === 'item' && sub.id
                    ? { item: sub.id }
                    : sub.kind === 'album'
                      ? { album: sub.name, artist: sub.artist }
                      : { artist: sub.name }
                }
                title={sub.name}
              />
            ) : undefined
          }
        >
          {sub.name}
        </PageTitle>
      )}

      {sub && (
        <div className="card p-3 mb-4 flex items-center gap-4" data-testid="insights-subject">
          {sub.image ? (
            <img
              src={sub.image}
              alt=""
              className={cx(
                'w-20 h-20 object-cover shrink-0 bg-raised',
                sub.kind === 'artist' ? 'rounded-full' : 'rounded-[var(--radius-card-inner)]',
              )}
            />
          ) : (
            <span className="w-20 h-20 rounded-[var(--radius-card-inner)] bg-raised flex items-center justify-center text-3xl shrink-0">
              {icon}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-muted">
              {cap(sub.kind === 'item' ? (m?.item_noun.replace(/s$/, '') ?? 'track') : sub.kind)}
            </div>
            {sub.artist && sub.kind !== 'artist' && (
              <Link {...artistTo(sub.artist)} className="text-sm text-fg-2 hover:underline block truncate">
                {sub.artist}
              </Link>
            )}
            <div className="text-xs text-faint mt-1 flex flex-wrap gap-x-2.5">
              <span>
                {fmtNum(sub.plays)} {unit} all time
              </span>
              {sub.first_at && (
                <span>
                  first {fmtDate(sub.first_at, { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
              {sub.last_at && (
                <span>last {fmtDate(sub.last_at, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              )}
            </div>
            {sub.genres && sub.genres.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {sub.genres.slice(0, 6).map((g) => (
                  <span key={g} className="text-[11px] rounded-full bg-raised border border-line px-2 py-0.5">
                    {g}
                  </span>
                ))}
              </div>
            )}
            {sub.url && (
              <a
                href={sub.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs underline text-muted mt-1 inline-block"
              >
                Open on Spotify ↗
              </a>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {metrics.length > 1 && !sub && (
          <Segmented
            label="Metric"
            value={q.metric ?? 'listened'}
            onChange={(v) => set({ metric: v })}
            options={metrics.map((a) => ({ value: a.key, label: a.label }))}
          />
        )}
        <PeriodPicker q={q} set={set} firstAt={data?.first_at} />
      </div>

      {error && (
        <Failed
          what={`the ${(metrics.find((x) => x.key === q.metric)?.label ?? 'listening').toLowerCase()} insights`}
          reason={error}
          onRetry={() => setNudge((n) => n + 1)}
          className="mb-5"
        />
      )}
      {!data && !error && <SkeletonInsights />}
      {data && data.totals.count === 0 && data.available.length === 0 && (
        <Empty icon="📈">
          Nothing to read yet. Connect Spotify or import a history in Settings, and this fills in.
        </Empty>
      )}
      {data && data.totals.count === 0 && data.available.length > 0 && (
        <Empty icon="📈">
          Nothing {sub ? `by ${sub.name} ` : ''}in {periodLabel}.
        </Empty>
      )}
      {data && data.totals.count > 0 && (
        <Busy busy={loading}>
          {data.highlights.length > 0 && (
            <div className="mb-5" data-testid="insights-highlights">
              <SectionTitle>{periodLabel}</SectionTitle>
              <ul className="group-list max-w-3xl">
                {data.highlights.map((h, i) => {
                  const lead = h.artist ? (
                    <Link {...artistTo(h.artist)} className="hover:underline">
                      {h.lead}
                    </Link>
                  ) : h.item_id ? (
                    <Link {...itemTo(h.item_id)} className="hover:underline">
                      {h.lead}
                    </Link>
                  ) : (
                    h.lead
                  )
                  return (
                    <li key={`${h.kind}-${i}`} className="px-3.5 py-3 flex items-start gap-3">
                      <span className="text-lg leading-none mt-0.5 shrink-0" aria-hidden>
                        {HIGHLIGHT_ICON[h.kind] ?? '•'}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[17px] font-semibold tracking-tight text-accent-text leading-snug">
                          {lead}
                        </span>
                        <span className="block text-sm text-fg-2 leading-snug mt-0.5">{h.text}</span>
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          <Tiles testId="insights-tiles">
            <Tile
              label={cap(isCum ? m!.value_unit : unit)}
              value={isCum ? fmtHours(data.totals.total) : fmtNum(data.totals.count)}
              sub={isCum ? `${fmtNum(data.totals.count)} ${unit}` : minutesSub(data.totals)}
              delta={{
                now: isCum ? data.totals.total : data.totals.count,
                before: isCum ? data.previous.total : data.previous.count,
              }}
            />
            <Tile
              label={cap(m!.item_noun)}
              value={fmtNum(data.totals.items)}
              sub={
                data.totals.groups
                  ? `${fmtNum(data.totals.groups)} ${one(data.totals.groups, `${m!.groups?.[0] ?? ''}s`)}`
                  : undefined
              }
              delta={{ now: data.totals.items, before: data.previous.items }}
            />
            <Tile
              label="Active days"
              value={fmtNum(data.totals.active_days)}
              sub={data.totals.per_day ? `${data.totals.per_day.toFixed(1)} ${unit} a day` : undefined}
              delta={{ now: data.totals.active_days, before: data.previous.active_days }}
            />
          </Tiles>

          <SectionTitle right={<span className="text-xs text-faint">by {data.bucket}</span>}>
            Over time
          </SectionTitle>
          <div className="card p-3 mb-5" data-testid="insights-series">
            <Area
              series={data.series}
              unit={unit}
              bucket={data.bucket}
              onPick={data.bucket === 'day' ? undefined : zoom}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-4 mb-5">
            <div>
              <SectionTitle>When</SectionTitle>
              <div className="card p-3">
                <Heatmap cells={data.heatmap} unit={isCum ? m!.value_unit : unit} />
              </div>
            </div>
            <div>
              <SectionTitle>Rhythm</SectionTitle>
              <div className="card p-3 flex flex-col gap-3">
                {data.parts.length > 0 && (
                  <ul className="flex flex-col gap-1.5" data-testid="insights-parts">
                    {data.parts.map((p) => (
                      <li key={p.part} className="text-sm">
                        <div className="flex items-baseline justify-between gap-2">
                          <span>
                            <span className="mr-1.5" aria-hidden>
                              {PARTS[p.part]!.icon}
                            </span>
                            {PARTS[p.part]!.label}
                            <span className="text-faint text-xs ml-1.5">{PARTS[p.part]!.hours}</span>
                          </span>
                          <span className="text-xs text-muted tabular-nums">
                            {Math.round(p.share * 100)}%
                          </span>
                        </div>
                        <div className="h-1 rounded-full bg-line mt-1 overflow-hidden">
                          <div
                            className="h-full bg-accent rounded-full"
                            style={{ width: p.count ? `${Math.max(2, p.share * 100)}%` : 0 }}
                          />
                        </div>
                        {p.top.length > 0 && (
                          <div className="text-xs text-faint mt-0.5 truncate">
                            {p.top.map((t, i) => (
                              <Fragment key={t.key}>
                                {i > 0 && ', '}
                                <Link {...artistTo(t.key)} className="hover:underline">
                                  {t.key}
                                </Link>
                              </Fragment>
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div
                  className={cx('text-xs text-faint', data.parts.length > 0 && 'border-t border-line pt-2')}
                  data-testid="insights-weekly"
                >
                  {data.weekly.weekday_per_day.toFixed(1)} {unit} on a weekday,{' '}
                  {data.weekly.weekend_per_day.toFixed(1)} on a weekend day
                  {data.weekly.weekend_per_day > data.weekly.weekday_per_day * 1.15
                    ? ': more at weekends.'
                    : data.weekly.weekday_per_day > data.weekly.weekend_per_day * 1.15
                      ? ': more in the week.'
                      : '.'}
                </div>
              </div>
            </div>
          </div>

          {data.sessions && data.sessions.count > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5" data-testid="insights-sessions">
              <div>
                <SectionTitle
                  right={
                    <span className="text-xs text-faint">
                      {sub ? `sittings with ${sub.name} in them` : 'a gap of 30 minutes ends one'}
                    </span>
                  }
                >
                  Sessions
                </SectionTitle>
                <div className="card grid grid-cols-3 divide-x divide-line overflow-hidden mb-2">
                  <Tile label="Sessions" value={fmtNum(data.sessions.count)} />
                  <Tile
                    label="Typical"
                    value={`${Math.round(data.sessions.avg_plays)} ${unit}`}
                    sub={fmtHours(data.sessions.avg_minutes)}
                  />
                  <Tile
                    label="Longest"
                    value={data.sessions.longest ? fmtHours(data.sessions.longest.minutes) : '–'}
                    sub={
                      data.sessions.longest
                        ? `${fmtNum(data.sessions.longest.plays)} ${unit} on ${fmtDate(data.sessions.longest.started)}`
                        : undefined
                    }
                  />
                </div>
                <ul className="group-list">
                  {data.sessions.recent.map((s) => (
                    <SessionRow key={s.started} s={s} unit={unit} />
                  ))}
                </ul>
              </div>
              <div>
                <SectionTitle
                  right={
                    <span className="text-xs text-faint">mostly one album, six {unit} or more of it</span>
                  }
                >
                  Album binges
                </SectionTitle>
                <ul className="group-list">
                  {data.sessions.binges.map((s) => (
                    <SessionRow key={s.started} s={s} unit={unit} />
                  ))}
                  {data.sessions.binges.length === 0 && (
                    <li className="px-3 py-2 text-sm text-faint">No album sat through in one go here.</li>
                  )}
                </ul>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div>
              <SectionTitle>Most played</SectionTitle>
              <ul className="group-list" data-testid="insights-top-items">
                {data.top_items.slice(0, 10).map((t, i) => (
                  <BarRow
                    key={t.id}
                    image={t.data.image}
                    icon={icon}
                    title={t.title}
                    sub={String(t.data.artist ?? '')}
                    value={isCum ? fmtHours(t.total) : `${fmtNum(t.count)}×`}
                    share={
                      isCum
                        ? t.total / Math.max(1, data.top_items[0]!.total)
                        : t.count / Math.max(1, data.top_items[0]!.count)
                    }
                    to={sub?.kind === 'item' ? undefined : itemTo(t.id)}
                    testId={i === 0 ? 'insights-top-first' : undefined}
                    play={{ item: t.id }}
                    uri={typeof t.data.uri === 'string' ? t.data.uri : undefined}
                  />
                ))}
                {data.top_items.length === 0 && (
                  <li className="px-3 py-2 text-sm text-faint">Nothing in this window.</li>
                )}
              </ul>
            </div>
            {Object.entries(data.top_groups).map(([field, rows]) => (
              <div key={field}>
                <SectionTitle>{field.charAt(0).toUpperCase() + field.slice(1)}s</SectionTitle>
                <ul className="group-list" data-testid={`insights-top-${field}`}>
                  {rows.slice(0, 10).map((g) => (
                    <BarRow
                      key={g.key}
                      image={g.image}
                      icon={icon}
                      round={field === 'artist'}
                      title={g.key}
                      sub={
                        <>
                          {field === 'album' && g.artist && <span className="truncate">{g.artist}</span>}
                          <span className="shrink-0">
                            {fmtNum(g.items)} {one(g.items, m!.item_noun)}
                          </span>
                          {g.total >= 1 && <span className="shrink-0">{fmtHours(g.total)}</span>}
                        </>
                      }
                      value={`${fmtNum(g.count)}×`}
                      share={g.count / Math.max(1, rows[0]!.count)}
                      to={field === 'artist' ? artistTo(g.key) : albumTo(g.key, g.artist)}
                      play={field === 'artist' ? { artist: g.key } : { album: g.key, artist: g.artist }}
                    />
                  ))}
                  {rows.length === 0 && (
                    <li className="px-3 py-2 text-sm text-faint">Nothing in this window.</li>
                  )}
                </ul>
              </div>
            ))}
          </div>

          {data.genres.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5" data-testid="insights-genres">
              <div>
                <SectionTitle right={<span className="text-xs text-faint">from Last.fm's tags</span>}>
                  Genres
                </SectionTitle>
                <ul className="group-list">
                  {data.genres.slice(0, 10).map((g) => (
                    <li key={g.genre} className="px-3 py-2">
                      <div className="flex items-baseline justify-between gap-2 text-sm">
                        <span className="truncate">{g.genre}</span>
                        <span className="text-xs text-muted tabular-nums shrink-0">
                          {Math.round(g.share * 100)}%, {g.artists} {one(g.artists, 'artists')}
                        </span>
                      </div>
                      <div className="h-1 rounded-full bg-line mt-1.5 overflow-hidden">
                        <div
                          className="h-full bg-accent rounded-full"
                          style={{
                            width: `${Math.max(3, (g.count / Math.max(1, data.genres[0]!.count)) * 100)}%`,
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              {data.genre_series.length > 1 && (
                <div>
                  <SectionTitle>Genres over time</SectionTitle>
                  <div className="card p-3">
                    <GenreFlow
                      buckets={data.genre_series}
                      genres={data.genres.slice(0, 6).map((g) => g.genre)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {data.trends && (
            <div className="mb-5" data-testid="insights-trends">
              <SectionTitle
                right={
                  <span className="text-xs text-faint flex flex-wrap justify-end gap-x-2.5">
                    <span>the top ten take {Math.round(data.trends.loyalty * 100)}%</span>
                    <span>
                      {(data.trends.variety * 100).toFixed(0)} different in every 100 {unit}
                    </span>
                  </span>
                }
              >
                Trends
              </SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <TrendList title="Rising" rows={data.trends.rising} empty="Nobody doubled up." unit={unit} />
                <TrendList
                  title="Falling"
                  rows={data.trends.falling}
                  empty="Nobody dropped away."
                  unit={unit}
                />
                <TrendList
                  title="New here"
                  rows={data.trends.fresh}
                  empty="No newcomers with five plays."
                  unit={unit}
                />
                <div>
                  <SectionTitle>Back after a while</SectionTitle>
                  <ul className="group-list">
                    {data.trends.returning.map((r) => (
                      <li key={r.artist}>
                        <Link
                          {...artistTo(r.artist)}
                          className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-raised/60"
                        >
                          <span className="min-w-0">
                            <span className="block truncate">{r.artist}</span>
                            <span className="block text-xs text-faint">
                              {r.gap_days >= 365
                                ? `${(r.gap_days / 365).toFixed(1)} years`
                                : `${Math.round(r.gap_days / 30)} months`}{' '}
                              away, back {fmtDate(r.first_in)}
                            </span>
                          </span>
                          <span className="text-xs text-muted tabular-nums shrink-0">{fmtNum(r.count)}×</span>
                        </Link>
                      </li>
                    ))}
                    {data.trends.returning.length === 0 && (
                      <li className="px-3 py-2 text-sm text-faint">No long-lost returns.</li>
                    )}
                  </ul>
                </div>
              </div>
              {data.trends.months >= 2 && (
                <p className="text-xs text-faint mt-2">
                  {data.trends.constant.length > 0 ? (
                    <>
                      In the top five every one of the {data.trends.months} months:{' '}
                      {data.trends.constant.map((a, i) => (
                        <Fragment key={a}>
                          {i > 0 && ', '}
                          <Link {...artistTo(a)} className="underline">
                            {a}
                          </Link>
                        </Fragment>
                      ))}
                      .
                    </>
                  ) : (
                    `Nobody held the top five across all ${data.trends.months} months.`
                  )}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            {!sub || sub.kind === 'artist' ? (
              <div>
                <SectionTitle
                  right={
                    <span className="text-xs text-faint">
                      {data.days === 0 && !q.since
                        ? 'newest first'
                        : `${fmtNum(data.new_count)} first heard in this window`}
                    </span>
                  }
                >
                  {data.days === 0 && !q.since ? 'Recently discovered' : 'New to you'}
                </SectionTitle>
                <ul className="group-list" data-testid="insights-new">
                  {data.new_items.slice(0, 8).map((t) => (
                    <BarRow
                      key={t.id}
                      image={t.data.image}
                      icon={icon}
                      title={t.title}
                      sub={
                        <>
                          {t.data.artist && <span className="truncate">{String(t.data.artist)}</span>}
                          <span className="shrink-0">
                            first {fmtDate(t.first_at, { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </>
                      }
                      value={`${fmtNum(t.count)}×`}
                      share={t.count / Math.max(1, data.new_items[0]!.count)}
                      to={itemTo(t.id)}
                    />
                  ))}
                  {data.new_items.length === 0 && (
                    <li className="px-3 py-2 text-sm text-faint">Nothing new in this window.</li>
                  )}
                </ul>
              </div>
            ) : (
              <div />
            )}
            {data.by_year.length > 1 && (
              <div>
                <SectionTitle
                  right={
                    data.first_at ? (
                      <span className="text-xs text-faint">
                        since {fmtDate(data.first_at, { month: 'short', year: 'numeric' })}
                      </span>
                    ) : undefined
                  }
                >
                  By year
                </SectionTitle>
                <div className="card p-3">
                  <div className="flex items-end gap-1.5 h-28">
                    {data.by_year.map((y) => {
                      const max = Math.max(1, ...data.by_year.map((r) => r.count))
                      return (
                        <button
                          type="button"
                          key={y.year}
                          onClick={() =>
                            set({ days: undefined, since: `${y.year}-01-01`, until: `${y.year + 1}-01-01` })
                          }
                          className="flex-1 flex flex-col items-center gap-1 min-w-0 group"
                          title={`${y.year}: ${fmtNum(y.count)} ${unit}${y.total >= 1 ? `, ${fmtHours(y.total)}` : ''}. Click to open.`}
                        >
                          <span className="text-[10px] text-faint tabular-nums leading-none">
                            {y.count >= 1000 ? `${(y.count / 1000).toFixed(1)}k` : y.count}
                          </span>
                          <div className="w-full h-20 flex items-end">
                            <div
                              className="w-full rounded-t bg-accent group-hover:brightness-125"
                              style={{ height: `${Math.max(3, (y.count / max) * 100)}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-faint leading-none">{y.year}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {data.plays && data.plays.length > 0 && (
            <div className="mb-5">
              <SectionTitle right={<span className="text-xs text-faint">newest first</span>}>
                Play history
              </SectionTitle>
              <ul className="group-list" data-testid="insights-plays">
                {data.plays.map((p, i) => (
                  <li
                    key={`${p.at}-${i}`}
                    className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
                  >
                    <span>
                      {fmtDate(p.at, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <span className="text-xs text-faint tabular-nums flex gap-x-2.5">
                      <span>
                        {local(p.at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span>{p.source}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Busy>
      )}
    </div>
  )
}
